import type { CodeString } from '@/consts/codes';
import { z } from 'zod';

const menuItemSchema = z.object({
  Code: z.string().transform((val) => val as CodeString),
  AccessedAt: z.string().optional()
});

export const menuItemsDataSchema = z.object({
  MenuItems: z.array(menuItemSchema)
});

const settingSchema = z.object({
  Id: z.string(),
  Category: z.string(),
  Code: z.string().optional(),
  Name: z.string().optional(),
  Data: z.string(),
  UserId: z.string(),
  RecordVersion: z.number(),
  OwnerChanged: z.boolean().optional(),
  CompanyId: z.string().nullable().optional(),
  VesselId: z.string().nullable().optional(),
  VesselTypeId: z.string().nullable().optional(),
  // Audit fields returned by backend
  CreatedBy: z.string().optional(),
  CreatedDate: z.string().optional(),
  CreatedDateLocal: z.string().optional(),
  UpdatedBy: z.string().optional(),
  UpdatedDate: z.string().optional(),
  UpdatedDateLocal: z.string().optional(),
  UsrName: z.string().optional()
});

const apiResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    Data: dataSchema.nullable().optional(),
    Error: z.boolean().optional(),
    Errors: z.array(z.string()).optional()
  });

export const settingApiResponseSchema = apiResponseSchema(settingSchema);
export const settingsArrayApiResponseSchema = apiResponseSchema(z.array(settingSchema));

export const settingOperationResponseSchema = z.object({
  Data: z
    .object({
      MenuItems: z.array(z.object({ Code: z.string() }))
    })
    .nullable()
    .optional(),
  Error: z.boolean().optional(),
  Errors: z.array(z.string()).optional()
});

export type Setting = z.infer<typeof settingSchema>;
export type MenuItemsData = z.infer<typeof menuItemsDataSchema>;
export interface ApiResponse<T> {
  Data?: T;
  Error?: boolean;
  Errors?: string[];
}

// Grid Save Layout schemas - matches AG Grid ColumnState interface
const columnStateSchema = z.object({
  colId: z.string(),
  hide: z.boolean().nullable().optional(),
  width: z.number().optional(),
  flex: z.number().nullable().optional(),
  sort: z.enum(['asc', 'desc']).nullable().optional(),
  sortIndex: z.number().nullable().optional(),
  aggFunc: z.string().nullable().optional(),
  pivot: z.boolean().nullable().optional(),
  pivotIndex: z.number().nullable().optional(),
  pinned: z
    .union([z.literal('left'), z.literal('right'), z.boolean()])
    .nullable()
    .optional(),
  rowGroup: z.boolean().nullable().optional(),
  rowGroupIndex: z.number().nullable().optional()
});

export const gridSaveLayoutStateSchema = z.object({
  columnState: z.array(columnStateSchema)
});

export type ColumnStateData = z.infer<typeof columnStateSchema>;
export type GridSaveLayoutState = z.infer<typeof gridSaveLayoutStateSchema>;

export const freshnessThresholdsDataSchema = z.object({
  green: z.number().int().positive(),
  orange: z.number().int().positive()
});

export type FreshnessThresholdsData = z.infer<typeof freshnessThresholdsDataSchema>;

export const starredPortsDataSchema = z.object({
  portIds: z.array(z.string())
});

export type StarredPortsData = z.infer<typeof starredPortsDataSchema>;
